/**
 * Chart Data Normalizer
 *
 * Unified data normalization service for the multi-layer event chart.
 * Converts all data sources to a common format with:
 * - Probabilities as percentages (0-100)
 * - Aligned 1-minute time buckets
 * - Consistent data structure for Recharts
 */

// ============================================
// Types
// ============================================

export interface RawOddsPoint {
  timestamp: string;
  bookmaker: string;
  outcomeName: string;
  price: number;
  isSharp: boolean | null;
}

export interface RawPolymarketPoint {
  timestamp: string;
  outcome: string;
  midPrice: number;
  bidPrice?: number;
  askPrice?: number;
}

export interface RawSignalPoint {
  timestamp: string;
  chainValue: number;
  direction: 'home' | 'away' | 'over' | 'under';
  signalType?: string;
}

export interface NormalizedDataPoint {
  time: string;           // ISO timestamp (bucket start)
  timeNum: number;        // Unix timestamp for sorting

  // Sportsbook odds (as implied probability %)
  oddsHome: number | null;
  oddsAway: number | null;
  oddsSharpHome: number | null;   // Sharp book consensus
  oddsSharpAway: number | null;
  oddsSquareHome: number | null;  // Square book consensus
  oddsSquareAway: number | null;

  // Polymarket (as probability %)
  polyHome: number | null;
  polyAway: number | null;
  polySpread: number | null;      // Bid-ask spread %

  // Derived metrics
  gapHome: number | null;         // (poly - odds) home
  gapAway: number | null;         // (poly - odds) away

  // RLM signals
  signalStrength: number | null;  // -100 to +100
  signalDirection: 'home' | 'away' | null;
}

export interface ChartDataResult {
  data: NormalizedDataPoint[];
  meta: {
    startTime: Date;
    endTime: Date;
    bucketCount: number;
    oddsPointCount: number;
    polyPointCount: number;
    signalCount: number;
    homeTeam?: string;
    awayTeam?: string;
  };
}

// ============================================
// Constants
// ============================================

const BUCKET_SIZE_MS = 60 * 1000; // 1-minute buckets

// Sharp books list (move opposite to public)
const SHARP_BOOKS = ['pinnacle', 'betfair', 'circa', 'westgate', 'bookmaker'];

// ============================================
// Conversion Functions
// ============================================

/**
 * Convert decimal odds to implied probability percentage
 * Example: 2.0 -> 50%, 1.5 -> 66.67%
 */
export function decimalToImpliedProb(decimalOdds: number): number {
  if (decimalOdds <= 1) return 100;
  return (1 / decimalOdds) * 100;
}

/**
 * Convert American odds to implied probability percentage
 * Example: -110 -> 52.38%, +150 -> 40%
 */
export function americanToImpliedProb(americanOdds: number): number {
  if (americanOdds > 0) {
    return (100 / (americanOdds + 100)) * 100;
  } else {
    return (Math.abs(americanOdds) / (Math.abs(americanOdds) + 100)) * 100;
  }
}

/**
 * Detect odds format and convert to implied probability
 * - Negative values are always American
 * - Values >= 100 are American (positive American odds like +150)
 * - Values between 1 and 15 are typically decimal (1.50 to 15.00)
 * - Values between 15 and 100 could be either, but likely decimal
 */
export function oddsToImpliedProb(odds: number): number {
  // Negative = American odds (e.g., -125)
  if (odds < 0) {
    return americanToImpliedProb(odds);
  }

  // >= 100 = American odds (e.g., +150, +200)
  if (odds >= 100) {
    return americanToImpliedProb(odds);
  }

  // Between 1 and ~15 = Decimal odds (1.50 = -200, 15.00 = +1400)
  // Decimal odds are always > 1.0
  if (odds > 1 && odds < 100) {
    return decimalToImpliedProb(odds);
  }

  // Edge case: odds <= 1 means 100% probability
  return 100;
}

/**
 * Convert Polymarket midPrice (0-1) to percentage (0-100)
 */
export function polyPriceToPercent(midPrice: number): number {
  return midPrice * 100;
}

/**
 * Check if a bookmaker is considered "sharp"
 */
export function isSharpBook(bookmaker: string): boolean {
  return SHARP_BOOKS.includes(bookmaker.toLowerCase());
}

// ============================================
// Time Bucket Functions
// ============================================

/**
 * Round timestamp down to nearest bucket
 */
export function getBucketStart(timestamp: Date | string | number): number {
  const ts = typeof timestamp === 'number'
    ? timestamp
    : new Date(timestamp).getTime();
  return Math.floor(ts / BUCKET_SIZE_MS) * BUCKET_SIZE_MS;
}

/**
 * Generate all bucket timestamps between start and end
 */
export function generateBuckets(startTime: number, endTime: number): number[] {
  const buckets: number[] = [];
  let current = getBucketStart(startTime);
  const end = getBucketStart(endTime);

  while (current <= end) {
    buckets.push(current);
    current += BUCKET_SIZE_MS;
  }

  return buckets;
}

// ============================================
// Data Processing Functions
// ============================================

/**
 * Group odds data by time bucket
 */
function processOddsData(
  oddsHistory: RawOddsPoint[],
  homeTeam?: string,
  _awayTeam?: string
): Map<number, {
  home: number[];
  away: number[];
  sharpHome: number[];
  sharpAway: number[];
  squareHome: number[];
  squareAway: number[];
}> {
  const bucketData = new Map<number, {
    home: number[];
    away: number[];
    sharpHome: number[];
    sharpAway: number[];
    squareHome: number[];
    squareAway: number[];
  }>();

  for (const point of oddsHistory) {
    const bucket = getBucketStart(point.timestamp);

    if (!bucketData.has(bucket)) {
      bucketData.set(bucket, {
        home: [], away: [],
        sharpHome: [], sharpAway: [],
        squareHome: [], squareAway: []
      });
    }

    const data = bucketData.get(bucket)!;
    const impliedProb = oddsToImpliedProb(point.price);
    const isSharp = point.isSharp ?? isSharpBook(point.bookmaker);

    // Determine if this is home or away outcome
    const isHome = homeTeam
      ? point.outcomeName.toLowerCase().includes(homeTeam.toLowerCase())
      : point.outcomeName.toLowerCase().includes('home');

    if (isHome) {
      data.home.push(impliedProb);
      if (isSharp) {
        data.sharpHome.push(impliedProb);
      } else {
        data.squareHome.push(impliedProb);
      }
    } else {
      data.away.push(impliedProb);
      if (isSharp) {
        data.sharpAway.push(impliedProb);
      } else {
        data.squareAway.push(impliedProb);
      }
    }
  }

  return bucketData;
}

/**
 * Group Polymarket data by time bucket
 */
function processPolymarketData(
  polyHistory: RawPolymarketPoint[],
  homeTeam?: string
): Map<number, { home: number[]; away: number[]; spreads: number[] }> {
  const bucketData = new Map<number, { home: number[]; away: number[]; spreads: number[] }>();

  for (const point of polyHistory) {
    const bucket = getBucketStart(point.timestamp);

    if (!bucketData.has(bucket)) {
      bucketData.set(bucket, { home: [], away: [], spreads: [] });
    }

    const data = bucketData.get(bucket)!;
    const prob = polyPriceToPercent(point.midPrice);

    // Determine if this is home or away (Yes = home team wins)
    const isHome = homeTeam
      ? point.outcome.toLowerCase().includes(homeTeam.toLowerCase()) ||
        point.outcome.toLowerCase() === 'yes'
      : point.outcome.toLowerCase() === 'yes' ||
        point.outcome.toLowerCase().includes('home');

    if (isHome) {
      data.home.push(prob);
    } else {
      data.away.push(prob);
    }

    // Calculate spread if bid/ask available
    if (point.bidPrice !== undefined && point.askPrice !== undefined) {
      const spread = (point.askPrice - point.bidPrice) * 100;
      data.spreads.push(spread);
    }
  }

  return bucketData;
}

/**
 * Group signal data by time bucket
 */
function processSignalData(
  signals: RawSignalPoint[]
): Map<number, { strength: number; direction: 'home' | 'away' }> {
  const bucketData = new Map<number, { strength: number; direction: 'home' | 'away' }>();

  for (const signal of signals) {
    const bucket = getBucketStart(signal.timestamp);

    // Use strongest signal in each bucket
    const existing = bucketData.get(bucket);
    if (!existing || Math.abs(signal.chainValue) > Math.abs(existing.strength)) {
      bucketData.set(bucket, {
        strength: signal.chainValue,
        direction: signal.direction === 'home' || signal.direction === 'over' ? 'home' : 'away'
      });
    }
  }

  return bucketData;
}

/**
 * Calculate average of array, or null if empty
 */
function avg(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// ============================================
// Main Normalization Function
// ============================================

/**
 * Normalize all data sources into a unified chart format
 */
export function normalizeChartData(
  oddsHistory: RawOddsPoint[],
  polyHistory: RawPolymarketPoint[],
  signals: RawSignalPoint[] = [],
  options: {
    homeTeam?: string;
    awayTeam?: string;
    startTime?: Date;
    endTime?: Date;
  } = {}
): ChartDataResult {
  const { homeTeam, awayTeam } = options;

  // Process each data source
  const oddsData = processOddsData(oddsHistory, homeTeam, awayTeam);
  const polyData = processPolymarketData(polyHistory, homeTeam);
  const signalData = processSignalData(signals);

  // Determine time range
  const allTimestamps = [
    ...oddsHistory.map(p => new Date(p.timestamp).getTime()),
    ...polyHistory.map(p => new Date(p.timestamp).getTime()),
    ...signals.map(s => new Date(s.timestamp).getTime())
  ].filter(t => !isNaN(t));

  if (allTimestamps.length === 0) {
    return {
      data: [],
      meta: {
        startTime: new Date(),
        endTime: new Date(),
        bucketCount: 0,
        oddsPointCount: 0,
        polyPointCount: 0,
        signalCount: 0,
        homeTeam,
        awayTeam
      }
    };
  }

  const startTime = options.startTime?.getTime() ?? Math.min(...allTimestamps);
  const endTime = options.endTime?.getTime() ?? Math.max(...allTimestamps);

  // Generate all time buckets
  const buckets = generateBuckets(startTime, endTime);

  // Build normalized data points
  const data: NormalizedDataPoint[] = buckets.map(bucket => {
    const odds = oddsData.get(bucket);
    const poly = polyData.get(bucket);
    const signal = signalData.get(bucket);

    const oddsHome = odds ? avg(odds.home) : null;
    const oddsAway = odds ? avg(odds.away) : null;
    const polyHome = poly ? avg(poly.home) : null;
    const polyAway = poly ? avg(poly.away) : null;

    return {
      time: new Date(bucket).toISOString(),
      timeNum: bucket,

      // Sportsbook odds
      oddsHome,
      oddsAway,
      oddsSharpHome: odds ? avg(odds.sharpHome) : null,
      oddsSharpAway: odds ? avg(odds.sharpAway) : null,
      oddsSquareHome: odds ? avg(odds.squareHome) : null,
      oddsSquareAway: odds ? avg(odds.squareAway) : null,

      // Polymarket
      polyHome,
      polyAway,
      polySpread: poly ? avg(poly.spreads) : null,

      // Price gaps
      gapHome: (polyHome !== null && oddsHome !== null) ? polyHome - oddsHome : null,
      gapAway: (polyAway !== null && oddsAway !== null) ? polyAway - oddsAway : null,

      // Signals
      signalStrength: signal?.strength ?? null,
      signalDirection: signal?.direction ?? null
    };
  });

  return {
    data,
    meta: {
      startTime: new Date(startTime),
      endTime: new Date(endTime),
      bucketCount: buckets.length,
      oddsPointCount: oddsHistory.length,
      polyPointCount: polyHistory.length,
      signalCount: signals.length,
      homeTeam,
      awayTeam
    }
  };
}

// ============================================
// Data Downsampling (LTTB Algorithm)
// ============================================

/**
 * Largest Triangle Three Buckets (LTTB) downsampling
 * Preserves visual shape while reducing data points
 */
export function downsampleLTTB<T extends { timeNum: number }>(
  data: T[],
  targetPoints: number,
  valueAccessor: (d: T) => number | null
): T[] {
  if (data.length <= targetPoints) return data;
  if (targetPoints <= 2) return [data[0], data[data.length - 1]];

  const result: T[] = [data[0]]; // Always keep first point

  const bucketSize = (data.length - 2) / (targetPoints - 2);

  let a = 0; // Previous selected point index

  for (let i = 0; i < targetPoints - 2; i++) {
    // Calculate bucket range
    const rangeStart = Math.floor((i + 1) * bucketSize) + 1;
    const rangeEnd = Math.floor((i + 2) * bucketSize) + 1;
    const actualEnd = Math.min(rangeEnd, data.length - 1);

    // Calculate average of next bucket (for area calculation)
    let avgX = 0, avgY = 0, count = 0;
    for (let j = rangeStart; j < actualEnd; j++) {
      const val = valueAccessor(data[j]);
      if (val !== null) {
        avgX += data[j].timeNum;
        avgY += val;
        count++;
      }
    }
    if (count > 0) {
      avgX /= count;
      avgY /= count;
    }

    // Find point in current bucket that creates largest triangle
    const bucketStart = Math.floor(i * bucketSize) + 1;
    const bucketEnd = rangeStart;

    let maxArea = -1;
    let selectedIdx = bucketStart;

    const aVal = valueAccessor(data[a]);

    for (let j = bucketStart; j < bucketEnd; j++) {
      const val = valueAccessor(data[j]);
      if (val === null || aVal === null) continue;

      // Calculate triangle area
      const area = Math.abs(
        (data[a].timeNum - avgX) * (val - aVal!) -
        (data[a].timeNum - data[j].timeNum) * (avgY - aVal!)
      ) * 0.5;

      if (area > maxArea) {
        maxArea = area;
        selectedIdx = j;
      }
    }

    result.push(data[selectedIdx]);
    a = selectedIdx;
  }

  result.push(data[data.length - 1]); // Always keep last point

  return result;
}

/**
 * Downsample normalized data for chart rendering
 * Preserves important features like signal spikes
 */
export function downsampleChartData(
  data: NormalizedDataPoint[],
  targetPoints: number = 200
): NormalizedDataPoint[] {
  if (data.length <= targetPoints) return data;

  // For unified chart, downsample based on home gap (primary metric)
  return downsampleLTTB(data, targetPoints, d => d.gapHome);
}
